import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, Ban, CheckCircle2, FileText, Users, Download, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { formatMinor } from "../lib/commercial";
import { countryName } from "../i18n";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api, apiError } from "../lib/api";

const PLAN_LABEL = { free: "Gratuit", pro: "Pro", team: "Team", business: "Business", enterprise: "Entreprise" };
const CUSTOMER_TYPE_LABEL = { individual: "Particulier", business: "Entreprise" };

const DOLIBARR_STATUS = {
  pending: { label: "En attente", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  invoiced: { label: "Facturé", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  error: { label: "Erreur", cls: "bg-red-500/15 text-red-600 border-red-500/30" },
};

const emptyAddress = { line1: "", line2: "", city: "", postal_code: "", country_code: "" };

export const AdminClients = ({ section = "all" }) => {
  const { i18n } = useTranslation();
  const lng = i18n.language || "fr";
  const [clients, setClients] = useState(null);
  const [countries, setCountries] = useState([]);
  const [billing, setBilling] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [edit, setEdit] = useState(null);
  const [savingClient, setSavingClient] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const clientPromise = section !== "billing"
        ? Promise.all([api.get("/admin/workspaces"), api.get("/countries")])
        : Promise.resolve(null);
      const billingPromise = section !== "clients"
        ? api.get("/admin/billing")
        : Promise.resolve(null);

      const [clientResponses, billingResponse] = await Promise.all([clientPromise, billingPromise]);

      if (clientResponses) {
        setClients(clientResponses[0].data);
        setCountries(clientResponses[1].data.countries || []);
      }
      if (billingResponse) setBilling(billingResponse.data);
    } catch (e) {
      setLoadError(true);
    }
  }, [section]);

  useEffect(() => { load(); }, [load]);

  const changePlan = async (workspaceId, plan) => {
    try {
      await api.post(`/admin/workspaces/${workspaceId}/plan`, { plan });
      toast.success(`Forfait mis à jour : ${PLAN_LABEL[plan]}`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const toggleSuspend = async (workspaceId) => {
    try {
      const { data } = await api.post(`/admin/workspaces/${workspaceId}/suspend`);
      toast.success(data.suspended ? "Client suspendu" : "Client réactivé");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openEdit = (client) => {
    setEdit({
      id: client.id,
      public_ref: client.public_ref,
      name: client.name || "",
      customer_type: client.customer_type || "",
      company_name: client.company_name || "",
      trade_name: client.trade_name || "",
      registration_number: client.registration_number || "",
      vat_number: client.vat_number || "",
      company_phone: client.company_phone || "",
      billing_email: client.billing_email || "",
      billing_contact_name: client.billing_contact_name || "",
      invoice_to_owner: !!client.invoice_to_owner,
      billing_address: { ...emptyAddress, ...(client.billing_address || {}) },
    });
  };

  const saveClient = async () => {
    if (!edit?.customer_type) {
      toast.error("Choisissez le type de client.");
      return;
    }
    const a = edit.billing_address || emptyAddress;
    if (!a.line1.trim() || !a.city.trim() || !a.postal_code.trim() || !a.country_code.trim()) {
      toast.error("Complétez l'adresse de facturation et le pays.");
      return;
    }
    if (edit.customer_type === "business" && !edit.company_name.trim()) {
      toast.error("La raison sociale est requise pour une entreprise.");
      return;
    }
    const countryMeta = countries.find((c) => c.country_code === a.country_code);
    if (edit.customer_type === "business" && countryMeta?.registration_required && !edit.registration_number.trim()) {
      toast.error(`${countryMeta.registration_label || "Le numéro d’immatriculation"} est obligatoire pour ce pays.`);
      return;
    }

    setSavingClient(true);
    try {
      await api.put(`/admin/workspaces/${edit.id}/client`, {
        name: edit.name.trim() || null,
        customer_type: edit.customer_type,
        company_name: edit.customer_type === "business" ? edit.company_name.trim() : null,
        trade_name: edit.trade_name.trim() || null,
        registration_number: edit.registration_number.trim() || null,
        vat_number: edit.vat_number.trim() || null,
        company_phone: edit.company_phone.trim() || null,
        billing_email: edit.billing_email.trim() || null,
        billing_contact_name: edit.billing_contact_name.trim() || null,
        invoice_to_owner: !!edit.invoice_to_owner,
        billing_address: {
          line1: a.line1.trim(),
          line2: a.line2.trim() || null,
          city: a.city.trim(),
          postal_code: a.postal_code.trim(),
          country_code: a.country_code.trim().toUpperCase(),
        },
      });
      toast.success("Identité client mise à jour.");
      setEdit(null);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingClient(false);
    }
  };

  const setEditField = (field, value) => setEdit((e) => ({ ...e, [field]: value }));
  const setAddressField = (field, value) => setEdit((e) => ({
    ...e, billing_address: { ...(e.billing_address || emptyAddress), [field]: value },
  }));

  const sortedCountries = countries.slice().sort((a, b) =>
    countryName(a.country_code, lng).localeCompare(countryName(b.country_code, lng), lng)
  );
  const selectedCountry = edit
    ? countries.find((c) => c.country_code === edit.billing_address?.country_code) || null
    : null;

  const downloadInvoice = async (b) => {
    try {
      const res = await api.get(`/admin/invoices/${b.invoice_id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Facture-${b.invoice_number || b.invoice_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Impossible de télécharger la facture.");
    }
  };

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center" data-testid="admin-clients-view">
        <p className="text-slate-500">Impossible de charger les clients.</p>
        <Button onClick={load} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">Réessayer</Button>
      </div>
    );
  }

  if (section !== "billing" && !clients) {
    return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="space-y-6" data-testid="admin-clients-view"
    >
      {section !== "billing" && (
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-emerald-600" />
            <h3 className="font-heading text-lg font-semibold">Clients ({clients.length})</h3>
          </div>

          {clients.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">Aucun Workspace client pour le moment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="clients-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-3 pr-4 font-semibold">Réf.</th>
                    <th className="py-3 pr-4 font-semibold">Client / Société</th>
                    <th className="py-3 pr-4 font-semibold">Type</th>
                    <th className="py-3 pr-4 font-semibold">Région</th>
                    <th className="py-3 pr-4 font-semibold">Propriétaire</th>
                    <th className="py-3 pr-4 font-semibold">Sièges</th>
                    <th className="py-3 pr-4 font-semibold">Forfait</th>
                    <th className="py-3 pr-2 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => {
                    const identityMissing = c.identity_complete === false || !c.customer_type;
                    return (
                      <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 pr-4 font-mono-token text-xs text-slate-600 whitespace-nowrap">{c.public_ref || "—"}</td>
                        <td className="py-3 pr-4">
                          <div className="font-medium text-slate-900">{c.company_name || c.name || "—"}</div>
                          {identityMissing && (
                            <Badge variant="outline" className="mt-1 bg-amber-500/10 text-amber-700 border-amber-500/30 text-[10px]">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Identité à compléter
                            </Badge>
                          )}
                          {c.suspended && (
                            <Badge variant="outline" className="mt-1 ml-1 bg-red-500/15 text-red-600 border-red-500/30">Suspendu</Badge>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-slate-600 text-xs">{CUSTOMER_TYPE_LABEL[c.customer_type] || "—"}</td>
                        <td className="py-3 pr-4 text-slate-600 text-xs">{c.commercial_region || "—"}</td>
                        <td className="py-3 pr-4">
                          <div className="text-slate-700 truncate max-w-[180px]">{c.owner_name || "—"}</div>
                          <div className="text-xs text-slate-500 truncate max-w-[180px]">{c.owner_email || "—"}</div>
                        </td>
                        <td className="py-3 pr-4 text-slate-600 font-mono-token whitespace-nowrap">{c.seats_used}/{c.seats_purchased}</td>
                        <td className="py-3 pr-4">
                          <Select value={c.plan} onValueChange={(v) => changePlan(c.id, v)}>
                            <SelectTrigger data-testid={`client-plan-select-${c.id}`} className="h-8 w-32 bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.keys(PLAN_LABEL).map((p) => <SelectItem key={p} value={p}>{PLAN_LABEL[p]}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <div className="mt-1 text-[10px] text-slate-500">{c.status_label || c.status || "—"}</div>
                        </td>
                        <td className="py-3 pr-2 text-right whitespace-nowrap">
                          <Button data-testid={`client-edit-btn-${c.id}`} onClick={() => openEdit(c)} size="sm" variant="outline" className="border-slate-200 bg-white text-slate-700 mr-2" title="Modifier le client">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`client-suspend-btn-${c.id}`}
                            onClick={() => toggleSuspend(c.id)}
                            size="sm" variant="outline"
                            className={c.suspended
                              ? "border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600"
                              : "border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-600"}
                          >
                            {c.suspended ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!edit} onOpenChange={(open) => !open && !savingClient && setEdit(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="client-edit-dialog">
          <DialogHeader>
            <DialogTitle>Identité client · {edit?.public_ref}</DialogTitle>
            <DialogDescription>Ces informations appartiennent au Workspace. Le pays provient du référentiel SecurePass et détermine automatiquement la région commerciale.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Nom du Workspace</Label>
                  <Input value={edit.name} onChange={(e) => setEditField("name", e.target.value)} className="bg-slate-50 border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Type de client</Label>
                  <Select value={edit.customer_type || undefined} onValueChange={(v) => setEditField("customer_type", v)}>
                    <SelectTrigger className="bg-slate-50 border-slate-200"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Particulier</SelectItem>
                      <SelectItem value="business">Entreprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {edit.customer_type === "business" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Raison sociale</Label>
                    <Input value={edit.company_name} onChange={(e) => setEditField("company_name", e.target.value)} className="bg-slate-50 border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">{selectedCountry?.registration_label || "N° d’immatriculation"}</Label>
                    <Input value={edit.registration_number} onChange={(e) => setEditField("registration_number", e.target.value)} className="bg-slate-50 border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">N° TVA</Label>
                    <Input value={edit.vat_number} onChange={(e) => setEditField("vat_number", e.target.value)} className="bg-slate-50 border-slate-200" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Nom commercial</Label>
                  <Input value={edit.trade_name || ""} onChange={(e) => setEditField("trade_name", e.target.value)} className="bg-slate-50 border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Téléphone société</Label>
                  <Input value={edit.company_phone || ""} onChange={(e) => setEditField("company_phone", e.target.value)} className="bg-slate-50 border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">E-mail de facturation</Label>
                  <Input type="email" value={edit.billing_email || ""} onChange={(e) => setEditField("billing_email", e.target.value)} className="bg-slate-50 border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Contact facturation</Label>
                  <Input value={edit.billing_contact_name || ""} onChange={(e) => setEditField("billing_contact_name", e.target.value)} className="bg-slate-50 border-slate-200" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={!!edit.invoice_to_owner} onChange={(e) => setEditField("invoice_to_owner", e.target.checked)} />
                Envoyer également les factures au propriétaire du Workspace
              </label>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="font-medium text-sm text-slate-800">Adresse de facturation</div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Adresse</Label>
                  <Input value={edit.billing_address.line1 || ""} onChange={(e) => setAddressField("line1", e.target.value)} className="bg-white border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Complément</Label>
                  <Input value={edit.billing_address.line2 || ""} onChange={(e) => setAddressField("line2", e.target.value)} className="bg-white border-slate-200" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Ville</Label>
                    <Input value={edit.billing_address.city || ""} onChange={(e) => setAddressField("city", e.target.value)} className="bg-white border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Code postal</Label>
                    <Input value={edit.billing_address.postal_code || ""} onChange={(e) => setAddressField("postal_code", e.target.value)} className="bg-white border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Pays</Label>
                    <select
                      data-testid="client-country-select"
                      value={edit.billing_address.country_code || ""}
                      onChange={(e) => setAddressField("country_code", e.target.value)}
                      className="w-full rounded-md bg-white border border-slate-200 text-slate-900 px-3 py-2 text-sm"
                    >
                      <option value="">Choisir un pays…</option>
                      {sortedCountries.map((c) => (
                        <option key={c.country_code} value={c.country_code}>
                          {countryName(c.country_code, lng)} ({c.country_code})
                        </option>
                      ))}
                    </select>
                    {selectedCountry && (
                      <div className="mt-1 text-[10px] text-slate-500">
                        Référentiel SecurePass · ISO {selectedCountry.country_code}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" disabled={savingClient} onClick={() => setEdit(null)}>Annuler</Button>
                <Button data-testid="client-save-btn" disabled={savingClient} onClick={saveClient} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                  {savingClient && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {section !== "clients" && (
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-cyan-600" />
            <h3 className="font-heading text-lg font-semibold">Facturation (Dolibarr)</h3>
          </div>
          {billing.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">Aucune facturation déclenchée.</p>
          ) : (
            <div className="space-y-2 max-h-[360px] overflow-y-auto" data-testid="billing-list">
              {billing.map((b) => {
                const st = DOLIBARR_STATUS[b.dolibarr_status] || DOLIBARR_STATUS.pending;
                return (
                  <div key={b.id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm text-slate-900 truncate">
                        {b.email} · <span className="font-semibold">{b.plan_name}</span> · {b.amount_minor != null && b.currency ? formatMinor(b.amount_minor, b.currency) : (b.amount_eur != null ? `${b.amount_eur}€` : "—")}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(b.created_at).toLocaleString("fr-FR")} · déclenché par {b.actor}
                        {b.invoice_number ? ` · ${b.invoice_number}` : ""}
                        {b.invoice_ref ? ` · Dolibarr #${b.invoice_ref}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {b.invoice_id && (
                        <Button data-testid={`admin-invoice-download-${b.invoice_id}`} onClick={() => downloadInvoice(b)} size="sm" variant="outline" className="border-slate-200 bg-white hover:bg-slate-100 text-slate-900">
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      <Badge variant="outline" className={st.cls}>{st.label}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};
